import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, MapPin, Calendar, Feather, ArrowRight } from "lucide-react";
import api from "../lib/api";
import { Spinner, SectionLabel, Pill } from "../components/Primitives";
import { photoSrc } from "./Stories";

const CATS = {
  kingdom_prosperity: { label: "Kingdom Prosperity", tone: "gold", border: "border-gold", dot: "bg-gold" },
  exploitation: { label: "Exploitation", tone: "blood", border: "border-blood", dot: "bg-blood" },
  resistance: { label: "Resistance", tone: "forest", border: "border-forest", dot: "bg-[#3a7a3d]" },
  heritage_story: { label: "Heritage Story", tone: "muted", border: "border-[#6d5ac4]", dot: "bg-[#8b7cd8]" },
};

const ALL = "All regions";

export default function Timeline() {
  const [events, setEvents] = useState([]);
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [region, setRegion] = useState(ALL);
  const [active, setActive] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/timeline"), api.get("/stories")])
      .then(([t, s]) => { setEvents(t.data); setStories(s.data); })
      .finally(() => setLoading(false));
  }, []);

  // Merge timeline milestones with heritage stories (those with a year + region), sorted chronologically.
  const merged = useMemo(() => {
    const evs = events.map((e) => ({ ...e, kind: e.category }));
    const sts = stories
      .filter((s) => s.year != null && s.region)
      .map((s) => ({ ...s, category: "heritage_story", kind: "heritage_story" }));
    return [...evs, ...sts].sort((a, b) => (a.year ?? 0) - (b.year ?? 0));
  }, [events, stories]);

  const regions = useMemo(() => {
    const set = new Set(merged.map((m) => m.region).filter(Boolean));
    return [ALL, ...Array.from(set).sort()];
  }, [merged]);

  const items = region === ALL ? merged : merged.filter((m) => m.region === region);

  return (
    <div data-testid="timeline-page">
      <div className="border-b border-edge bg-ash">
        <div className="mx-auto max-w-7xl px-6 py-20 md:px-12">
          <SectionLabel>A Living Timeline</SectionLabel>
          <h1 className="font-serif text-5xl font-semibold text-bone lg:text-6xl">A Timeline of Truth</h1>
          <p className="mt-5 max-w-2xl text-lg text-muted">
            From the founding of Mali to the abolition of slavery — prosperity, exploitation, and resistance,
            woven together with the personal <Link to="/stories" className="text-gold hover:underline">heritage stories</Link> you share.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <div className="flex flex-wrap gap-3">
              {Object.values(CATS).map((c) => (
                <span key={c.label} className="flex items-center gap-2 text-xs text-muted">
                  <span className={`h-2.5 w-2.5 ${c.dot}`} /> {c.label}
                </span>
              ))}
            </div>
          </div>

          {/* REGION FILTER */}
          <div className="mt-6">
            <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.15em] text-muted">Filter by region / tribe</label>
            <div className="flex flex-wrap gap-2" data-testid="region-filter">
              {regions.map((r) => (
                <button
                  key={r}
                  onClick={() => setRegion(r)}
                  data-testid={`region-${r.replace(/\s+/g, "-").toLowerCase()}`}
                  className={`border px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.12em] transition-colors ${
                    region === r ? "border-gold bg-gold text-ink" : "border-edge text-bone hover:border-gold/60"
                  }`}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <Spinner />
      ) : (
        <div className="mx-auto max-w-4xl px-6 py-16 md:px-12">
          <p className="mb-8 font-mono text-xs uppercase tracking-[0.15em] text-muted" data-testid="timeline-count">
            {items.length} milestone{items.length === 1 ? "" : "s"}{region !== ALL ? ` in ${region}` : ""}
          </p>
          <div className="relative">
            <div className="absolute left-3 top-0 h-full w-px bg-edge md:left-1/2" />
            {items.map((ev, i) => {
              const cat = CATS[ev.kind] || CATS.kingdom_prosperity;
              const left = i % 2 === 0;
              const isStory = ev.kind === "heritage_story";
              return (
                <motion.button
                  key={ev.id || `${ev.title}-${i}`}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5 }}
                  onClick={() => setActive(ev)}
                  className={`relative mb-10 block w-full pl-12 text-left md:w-1/2 md:pl-0 ${left ? "md:pr-12" : "md:ml-auto md:pl-12"}`}
                  data-testid={`timeline-event-${i}`}
                >
                  <span className={`absolute left-1.5 top-2 h-3 w-3 rounded-full ${cat.dot} ring-4 ring-ink md:left-auto ${left ? "md:-right-1.5" : "md:-left-1.5"}`} />
                  <div className={`group border-l-2 ${cat.border} bg-ash p-6 transition-colors hover:bg-[#211f1a]`}>
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-mono text-sm tracking-[0.1em] text-gold">{ev.year_label}</span>
                      <Pill tone={cat.tone}>{cat.label}</Pill>
                    </div>
                    <h3 className="mt-3 flex items-center gap-2 font-serif text-2xl text-bone">
                      {isStory && <Feather className="h-4 w-4 flex-shrink-0 text-[#8b7cd8]" />}
                      {ev.title}
                    </h3>
                    <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.12em] text-muted">
                      {ev.region}{isStory && ev.author_name ? ` · ${ev.author_name}` : ""}
                    </p>
                    <p className="mt-3 line-clamp-2 text-sm leading-relaxed text-[#cfc9bd]">{ev.description || ev.body}</p>
                    <span className="mt-3 inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-[0.12em] text-gold opacity-0 transition-opacity group-hover:opacity-100">
                      View details <ArrowRight className="h-3 w-3" />
                    </span>
                  </div>
                </motion.button>
              );
            })}
          </div>
        </div>
      )}

      <AnimatePresence>
        {active && <MilestoneModal item={active} onClose={() => setActive(null)} />}
      </AnimatePresence>
    </div>
  );
}

function MilestoneModal({ item, onClose }) {
  const cat = CATS[item.kind] || CATS.kingdom_prosperity;
  const isStory = item.kind === "heritage_story";
  return (
    <motion.div
      className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto bg-black/70 p-4 backdrop-blur-sm"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose} data-testid="milestone-modal"
    >
      <motion.div
        onClick={(e) => e.stopPropagation()}
        initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
        className="my-10 w-full max-w-2xl border border-edge bg-ink"
      >
        {isStory && item.photo_url && (
          <div className="max-h-80 overflow-hidden border-b border-edge">
            <img src={photoSrc(item.photo_url)} alt="" className="h-full w-full object-cover" />
          </div>
        )}
        <div className="p-6 md:p-8">
          <div className="flex items-start justify-between gap-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="flex items-center gap-1.5 font-mono text-sm tracking-[0.1em] text-gold">
                <Calendar className="h-4 w-4" /> {item.year_label}
              </span>
              <Pill tone={cat.tone}>{cat.label}</Pill>
            </div>
            <button onClick={onClose} data-testid="milestone-modal-close" className="text-muted hover:text-bone"><X className="h-6 w-6" /></button>
          </div>
          <h2 className="mt-4 font-serif text-3xl text-bone" data-testid="milestone-modal-title">{item.title}</h2>
          <p className="mt-2 flex items-center gap-2 text-sm text-muted">
            <MapPin className="h-4 w-4" /> {item.region}{isStory && item.author_name ? ` · Shared by ${item.author_name}` : ""}
          </p>
          <p className="mt-5 whitespace-pre-line leading-relaxed text-[#cfc9bd]">{item.description || item.body}</p>
          {isStory && (
            <Link to="/stories" className="mt-8 inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-[0.12em] text-gold hover:underline">
              More heritage stories <ArrowRight className="h-3 w-3" />
            </Link>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
